import { createRouter, RouteRecordRaw, createWebHistory } from "vue-router";
import store from "@/store";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import JwtService from "@/core/services/JwtService";
import { UserStatus } from "@/types/enum/UserStatus";
import { isLocaleSupported, setLocale } from "@/core/helpers/translation";
import i18n from "@/core/plugins/i18n";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/auth",
    component: () => import("@/components/page-layouts/Auth.vue"),
    children: [
      {
        path: "sign-in",
        name: "SignIn",
        component: () => import("@/views/Attorney/Auth/SignIn.vue"),
      },
      {
        path: "sign-up",
        name: "SignUp",
        component: () => import("@/views/Attorney/Auth/SignUp.vue"),
      },
      {
        path: "confirm-sign-up",
        name: "ConfirmSignUp",
        component: () => import("@/views/Attorney/Auth/ConfirmSignUp.vue"),
      },
      {
        path: "confirm-mail",
        name: "ConfirmMailMessage",
        component: () => import("@/views/Attorney/Auth/ConfirmMailMessage.vue"),
      },
      {
        path: "forgot-password",
        name: "ForgotPassword",
        component: () => import("@/views/Attorney/Auth/ForgotPassword.vue"),
      },
      {
        path: "reset-password",
        name: "ResetPassword",
        component: () => import("@/views/Attorney/Auth/ResetPassword.vue"),
      },
      {
        path: "mfa",
        name: "MFA",
        component: () => import("@/views/Attorney/Auth/MFA.vue"),
      },
    ],
  },
  {
    path: "/",
    redirect: "/overview",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/profile",
        name: "Profile",
        component: () => import("@/views/Attorney/UserProfile/Profile.vue"),
        children: [
          {
            path: "contact-details",
            name: "ProfileContactDetails",
            component: () =>
              import("@/views/Attorney/UserProfile/ContactDetails.vue"),
          },
          {
            path: "biography",
            name: "ProfileBiography",
            component: () =>
              import("@/views/Attorney/UserProfile/Biography.vue"),
          },
          {
            path: "public-profile",
            name: "PublicProfile",
            component: () =>
              import("@/views/Attorney/UserProfile/PublicProfile.vue"),
          },
          {
            path: "settings",
            name: "ProfileSettings",
            component: () =>
              import("@/views/Attorney/UserProfile/Settings.vue"),
          },
          {
            path: "payouts",
            name: "ProfilePayouts",
            component: () => import("@/views/Attorney/UserProfile/Payouts.vue"),
          },
          {
            path: "payments",
            name: "ProfilePayments",
            component: () =>
              import("@/views/Attorney/UserProfile/Payments.vue"),
          },
        ],
      },
      {
        path: "/overview",
        name: "overview",
        component: () => import("@/views/Attorney/Overview/Overview.vue"),
      },
      {
        path: "/jobs",
        name: "myJobs",
        component: () => import("@/views/Attorney/Jobs/Jobs.vue"),
      },
      {
        path: "/jobs/:id",
        name: "Job",
        component: () => import("@/views/Attorney/Jobs/Job.vue"),
        children: [
          {
            path: "home",
            name: "JobHome",
            component: () => import("@/views/Attorney/Jobs/Home.vue"),
          },
          {
            path: "overview",
            name: "JobOverview",
            component: () => import("@/views/Attorney/Jobs/JobOverview.vue"),
          },
          {
            path: "messages",
            name: "JobMessages",
            component: () => import("@/views/Attorney/Jobs/JobMessages.vue"),
          },
          {
            path: "documents",
            name: "JobDocuments",
            component: () => import("@/views/Attorney/Jobs/JobDocuments.vue"),
          },
          {
            path: "payments",
            name: "JobPayments",
            component: () => import("@/views/Attorney/Jobs/JobPayments.vue"),
          },
          {
            path: "activity-timeline",
            name: "JobActivityTimeline",
            component: () =>
              import("@/views/Attorney/Jobs/JobActivityTimeline.vue"),
          },
          {
            path: "review",
            name: "JobReview",
            component: () => import("@/views/Attorney/Jobs/JobReview.vue"),
          },
          {
            path: "documents/add",
            name: "AddAndSignDocument",
            component: () =>
              import(
                "@/views/Attorney/Jobs/SignDocuments/AddAndSignDocument.vue"
              ),
          },
          {
            path: "documents/:documentId/view-document",
            name: "ViewDocument",
            component: () =>
              import("@/views/Attorney/Jobs/SignDocuments/ViewDocument.vue"),
          },
          {
            path: "documents/:documentId",
            name: "UpdateDocument",
            component: () =>
              import("@/views/Attorney/Jobs/SignDocuments/UpdateDocument.vue"),
          },
        ],
      },
      {
        path: "/available-jobs",
        name: "AvailableJobs",
        component: () =>
          import("@/views/Attorney/BidForAJob/AvailableJobs.vue"),
        // children: [
        //   {
        //     path: ":id",
        //     name: "AvailableJobDetails",
        //     component: () =>
        //       import("@/views/Attorney/BidForAJob/JobDetails.vue"),
        //   },
        // ],
      },
      {
        path: "/available-jobs/:id/bid-for-a-job/:offerId?",
        name: "BidForAJobs",
        component: () => import("@/views/Attorney/BidForAJob/BidForAJob.vue"),
      },
      {
        path: "/products",
        name: "Products",
        component: () => import("@/views/Attorney/Products/Products.vue"),
      },
      {
        path: "/my-products",
        name: "MyProducts",
        component: () => import("@/views/Attorney/Products/MyProducts.vue"),
      },
      {
        path: "/products/:productId/add/:singleOfferId?",
        name: "ProductDetails",
        component: () => import("@/views/Attorney/Products/ProductDetails.vue"),
      },
      {
        path: "/documents",
        name: "Documents",
        component: () => import("@/views/Attorney/Document/Documents.vue"),
      },
      {
        path: "/payments",
        name: "Payments",
        component: () => import("@/views/Attorney/Payment/Payments.vue"),
      },
      {
        path: "/payments/:id",
        name: "PaymentDetails",
        component: () => import("@/views/Attorney/Payment/PaymentDetails.vue"),
      },
      {
        path: "/payouts",
        name: "Payouts",
        component: () => import("@/views/Attorney/Payment/Payouts.vue"),
      },
      {
        path: "/support-tickets",
        name: "SupportTickets",
        component: () =>
          import("@/views/Attorney/SupportTicket/SupportTickets.vue"),
      },
      {
        path: "/support-tickets/:id",
        name: "SupportTicketDetails",
        component: () =>
          import("@/views/Attorney/SupportTicket/SupportTicketDetails.vue"),
      },
      {
        path: "/about",
        name: "About",
        component: () => import("@/views/Attorney/About.vue"),
      },
      {
        path: "/terms-and-conditions",
        name: "TermsAndConditions",
        component: () => import("@/views/Attorney/TermsAndConditions.vue"),
      },
      {
        path: "/privacy-policy",
        name: "PrivacyPolicy",
        component: () => import("@/views/Attorney/PrivacyPolicy.vue"),
      },
      {
        path: "/contact-us",
        name: "ContactUs",
        component: () => import("@/views/Attorney/ContactUs.vue"),
      },
    ],
  },

  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    component: () => import("@/views/Attorney/Error404.vue"),
  },
  {
    path: "/500",
    name: "500",
    component: () => import("@/views/Attorney/Error500.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (next, prev) => {
  const errorRoutes = ["403", "404", "500"];

  // Check language for auth routes
  if (next.fullPath.includes("/auth")) {
    let lang = next.query.lang as any;
    if (!lang || !isLocaleSupported(lang)) {
      lang = "en";
    }
    setLocale(lang);
    i18n.global.locale.value = lang;

    if (!lang) {
      return { ...next, query: { ...next.query, lang: "en" } };
    }
  }

  // if route not auth.
  if (!next.fullPath.includes("auth")) {
    if (errorRoutes.indexOf(next.name as "") == -1) {
      localStorage.setItem(
        "public_suits_client_route_must_go_to_after_login",
        JSON.stringify({
          name: next.name,
          params: next.params,
          query: next.query,
        })
      );
    }
  }

  const allowedRoutesWithoutAuth = [
    "SignIn",
    "SignUp",
    "ConfirmSignUp",
    "ConfirmMailMessage",
    "ForgotPassword",
    "ResetPassword",
    "MFA",
    "About",
    "TermsAndConditions",
    "PrivacyPolicy",
    "ContactUs",
  ];

  const currentRoute = next.name as string;

  // reset config to initial state
  store.commit(Mutations.RESET_LAYOUT_CONFIG);

  const token = JwtService.getToken();

  if (token && next.name === "SignIn") {
    router.push({ name: "Profile" });
  } else if (token) {
    store
      .dispatch(Actions.GET_USER_TYPE)
      .then(() => {
        //
      })
      .catch(() => {
        //
      });

    let fcmToken = "";

    // if user account is active get permission.
    if (store.getters.currentUser.status == UserStatus.ACTIVE) {
      store
        .dispatch(Actions.REQUEST_PERMISSION)
        .then(() => {
          return store.dispatch(Actions.GET_OR_REFRESH_FCM_TOKEN);
        })
        .then((response) => {
          fcmToken = response.token;
          return store.dispatch(Actions.GET_USERS_IN_FIREBASE);
        })
        .then((users) => {
          const currentUser: any = store.getters.currentUser;
          users.forEach((user) => {
            // update only current user fcm token arrays.
            if (user.id == currentUser.user_id) {
              // update fcm tokens array if updated.
              const data = user.data();
              currentUser.fcm_token = fcmToken;
              store.commit(Mutations.SET_USER, currentUser);
              const idx = data.fcm_tokens.findIndex((token) => {
                return token == fcmToken;
              });
              // if generated token is new.
              if (idx == -1) {
                data.fcm_tokens.push(fcmToken);
                store.dispatch(Actions.UPDATE_FCM_TOKEN_IN_FIREBASE, {
                  userId: currentUser.user_id,
                  data,
                });
              }
            }
          });
        })
        .catch((error) => {
          // console.log("error:: ", error);
        });
    } else if (
      store.getters.currentUser.status == UserStatus.SUBMIT_REVIEW ||
      store.getters.currentUser.status == UserStatus.WAITING_REVIEW
    ) {
      const allowedRoutes = [
        "ProfileContactDetails",
        "ProfileBiography",
        "PublicProfile",
        "ProfileSettings",
      ];

      // when go to any not allowed route return to profile.
      if (
        allowedRoutes.findIndex((allowedRoute) => allowedRoute == next.name) ==
        -1
      ) {
        router.push({ name: "ProfileContactDetails" });
      }
    } else {
      store.commit(Mutations.PURGE_AUTH);
    }
  } else if (!token && allowedRoutesWithoutAuth.indexOf(currentRoute) === -1) {
    store.commit(Mutations.PURGE_AUTH);
  }

  // Scroll page to top on every route change
  setTimeout(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, 100);
});

router.onError((error) => {
  console.log("router err :: ", error);
  if (/ChunkLoadError:.*failed./i.test(error.message)) {
    window.location.reload();
  } else if (/Loading.*chunk.*failed./i.test(error.message)) {
    window.location.reload();
  } else if (/Syntax.*Error:.*Unexpected.*token.*<.*/i.test(error.message)) {
    window.location.reload();
  }
});

export default router;
